<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import * as Icon from "@heroicons/vue/24/solid";
import apiClient from "@/apiClient";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";

useBreadcrumbs().setBreadcrumb("Policies");

const route = useRoute();
const router = useRouter();
const policyTree = ref([]);
const currentFolderId = ref(null);
const loading = ref(true);
const search = ref("");
const notice = ref("");
const noticeIsError = ref(false);
const folderForm = ref({ name: "", saving: false, error: "" });
const moveForm = ref({ item: null, targetId: "", saving: false, error: "" });

const isPolicy = (item) => item?.kind === "policy";

const findNode = (nodes, id) => {
  for (const node of nodes || []) {
    if (Number(node.id) === Number(id)) return node;
    const child = findNode(node.children, id);
    if (child) return child;
  }
  return null;
};

const findPath = (nodes, id, path = []) => {
  for (const node of nodes || []) {
    const next = [...path, node];
    if (Number(node.id) === Number(id)) return next;
    const child = findPath(node.children, id, next);
    if (child) return child;
  }
  return null;
};

const flattenFolders = (nodes, depth = 0, result = []) => {
  for (const node of nodes || []) {
    if (!isPolicy(node)) {
      result.push({ ...node, depth });
      flattenFolders(node.children, depth + 1, result);
    }
  }
  return result;
};

const currentFolder = computed(() =>
  currentFolderId.value
    ? findNode(policyTree.value, currentFolderId.value)
    : null,
);
const currentItems = computed(() =>
  currentFolderId.value
    ? currentFolder.value?.children || []
    : policyTree.value,
);
const breadcrumbPath = computed(() =>
  currentFolderId.value
    ? findPath(policyTree.value, currentFolderId.value) || []
    : [],
);
const visibleItems = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return currentItems.value;
  return currentItems.value.filter((item) =>
    String(item.name || "")
      .toLowerCase()
      .includes(query),
  );
});
const moveTargets = computed(() =>
  flattenFolders(policyTree.value).filter(
    (folder) => Number(folder.id) !== Number(moveForm.value.item?.id),
  ),
);

const loadTree = async () => {
  loading.value = true;
  try {
    const response = await apiClient.get("/api/policy-documents");
    policyTree.value = response.data || [];
    if (
      currentFolderId.value &&
      !findNode(policyTree.value, currentFolderId.value)
    ) {
      currentFolderId.value = null;
    }
  } catch {
    notice.value = "Unable to load policies.";
    noticeIsError.value = true;
  } finally {
    loading.value = false;
  }
};

const openItem = (item) => {
  if (isPolicy(item)) {
    router.push({ name: "policy-editor", params: { id: item.id } });
    return;
  }
  currentFolderId.value = item.id;
};

const createFolder = async () => {
  const name = folderForm.value.name.trim();
  folderForm.value.error = "";
  if (!name) {
    folderForm.value.error = "Enter a folder name.";
    return;
  }

  folderForm.value.saving = true;
  try {
    await apiClient.post("/api/policy-documents", {
      name,
      kind: "folder",
      parent_id: currentFolderId.value || null,
    });
    folderForm.value.name = "";
    notice.value = "Folder created.";
    noticeIsError.value = false;
    await loadTree();
  } catch (error) {
    folderForm.value.error =
      error?.response?.data?.message || "Unable to create the folder.";
  } finally {
    folderForm.value.saving = false;
  }
};

const startMove = (item) => {
  moveForm.value = {
    item,
    targetId: item.parent_id ? String(item.parent_id) : "",
    saving: false,
    error: "",
  };
};

const moveItem = async () => {
  const item = moveForm.value.item;
  if (!item) return;
  moveForm.value.saving = true;
  moveForm.value.error = "";
  try {
    await apiClient.put(`/api/policy-documents/${item.id}`, {
      parent_id: moveForm.value.targetId
        ? Number(moveForm.value.targetId)
        : null,
    });
    moveForm.value.item = null;
    notice.value = `${isPolicy(item) ? "Policy" : "Folder"} moved.`;
    noticeIsError.value = false;
    await loadTree();
  } catch (error) {
    moveForm.value.error =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      "Unable to move this item.";
  } finally {
    moveForm.value.saving = false;
  }
};

const deleteItem = async (item) => {
  if (!window.confirm(`Delete "${item.name}"?`)) return;
  try {
    await apiClient.delete(`/api/policy-documents/${item.id}`);
    notice.value = `${isPolicy(item) ? "Policy" : "Folder"} deleted.`;
    noticeIsError.value = false;
    await loadTree();
  } catch (error) {
    notice.value =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      "Unable to delete this item.";
    noticeIsError.value = true;
  }
};

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(value))
    : "—";

watch(currentFolderId, (id) => {
  const query = { ...route.query };
  if (id) query.folder = String(id);
  else delete query.folder;
  router.replace({ query });
});

onMounted(async () => {
  const requestedFolder = Number(route.query.folder);
  currentFolderId.value =
    Number.isFinite(requestedFolder) && requestedFolder > 0
      ? requestedFolder
      : null;
  await loadTree();
});
</script>

<template>
  <main class="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
    <header
      class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
    >
      <div>
        <p class="text-sm font-medium text-emerald-700">Manager workspace</p>
        <h1 class="text-2xl font-semibold text-slate-900">Policies</h1>
        <p class="mt-1 text-sm text-slate-600">
          Organise, review and publish policies without a draft approval queue.
        </p>
      </div>
      <button
        class="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
        type="button"
        @click="
          router.push({
            name: 'policy-new',
            query: { parent_id: currentFolderId || '' },
          })
        "
      >
        <Icon.PlusIcon class="h-5 w-5" />
        New policy
      </button>
    </header>

    <div
      v-if="notice"
      class="rounded-lg border px-4 py-3 text-sm"
      :class="
        noticeIsError
          ? 'border-red-200 bg-red-50 text-red-700'
          : 'border-emerald-200 bg-emerald-50 text-emerald-800'
      "
    >
      {{ notice }}
    </div>

    <section class="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div
        class="flex flex-col gap-4 border-b border-slate-200 p-4 lg:flex-row lg:items-end lg:justify-between"
      >
        <div>
          <nav
            class="flex flex-wrap items-center gap-2 text-sm"
            aria-label="Folder path"
          >
            <button
              class="font-medium text-emerald-700 hover:underline"
              @click="currentFolderId = null"
            >
              All policies
            </button>
            <template v-for="folder in breadcrumbPath" :key="folder.id">
              <Icon.ChevronRightIcon class="h-4 w-4 text-slate-400" />
              <button
                class="font-medium text-slate-700 hover:text-emerald-700"
                @click="currentFolderId = folder.id"
              >
                {{ folder.name }}
              </button>
            </template>
          </nav>
          <p class="mt-2 text-xs text-slate-500">
            Folders are organisational containers and do not have a publication
            status.
          </p>
        </div>
        <div class="flex flex-col gap-2 sm:flex-row">
          <label class="relative">
            <span class="sr-only">Search this folder</span>
            <Icon.MagnifyingGlassIcon
              class="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-slate-400"
            />
            <input
              v-model="search"
              class="w-full rounded-lg border border-slate-300 py-2 pl-10 pr-3 text-sm focus:border-emerald-500 focus:ring-emerald-500 sm:w-64"
              placeholder="Search this folder"
            />
          </label>
        </div>
      </div>

      <div class="border-b border-slate-200 bg-slate-50 p-4">
        <form
          class="flex flex-col gap-2 sm:flex-row sm:items-start"
          @submit.prevent="createFolder"
        >
          <div class="flex-1">
            <label class="sr-only" for="folder-name">New folder name</label>
            <input
              id="folder-name"
              v-model="folderForm.name"
              class="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-emerald-500"
              placeholder="New folder name"
            />
            <p v-if="folderForm.error" class="mt-1 text-xs text-red-600">
              {{ folderForm.error }}
            </p>
          </div>
          <button
            class="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            :disabled="folderForm.saving"
          >
            <Icon.FolderPlusIcon class="h-5 w-5 text-amber-500" />
            {{ folderForm.saving ? "Creating…" : "Create folder" }}
          </button>
        </form>
      </div>

      <div v-if="loading" class="p-10 text-center text-sm text-slate-500">
        Loading policies…
      </div>
      <div v-else-if="!visibleItems.length" class="p-10 text-center">
        <Icon.FolderOpenIcon class="mx-auto h-10 w-10 text-slate-300" />
        <p class="mt-2 text-sm font-medium text-slate-700">
          This folder is empty
        </p>
        <p class="text-sm text-slate-500">
          Create a folder or publish a new policy here.
        </p>
      </div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-slate-200">
          <thead
            class="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
          >
            <tr>
              <th class="px-4 py-3">Name</th>
              <th class="px-4 py-3">Type</th>
              <th class="px-4 py-3">Publication</th>
              <th class="px-4 py-3">Updated</th>
              <th class="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 bg-white">
            <tr
              v-for="item in visibleItems"
              :key="item.id"
              class="hover:bg-slate-50"
            >
              <td class="px-4 py-3">
                <button
                  class="flex items-center gap-3 text-left font-medium text-slate-900 hover:text-emerald-700"
                  @click="openItem(item)"
                >
                  <component
                    :is="
                      isPolicy(item) ? Icon.DocumentTextIcon : Icon.FolderIcon
                    "
                    class="h-6 w-6 shrink-0"
                    :class="
                      isPolicy(item) ? 'text-emerald-600' : 'text-amber-500'
                    "
                  />
                  {{ item.name }}
                </button>
              </td>
              <td class="px-4 py-3 text-sm text-slate-600">
                {{ isPolicy(item) ? "Policy" : "Folder" }}
              </td>
              <td class="px-4 py-3 text-sm">
                <span
                  v-if="isPolicy(item)"
                  class="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold"
                  :class="
                    item.publication_status === 'published'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-slate-100 text-slate-600'
                  "
                >
                  {{
                    item.publication_status === "published"
                      ? "Published"
                      : "Not published"
                  }}
                </span>
                <span v-else class="text-slate-400">—</span>
              </td>
              <td class="px-4 py-3 text-sm text-slate-600">
                {{ formatDate(item.updated_at) }}
              </td>
              <td class="px-4 py-3">
                <div class="flex justify-end gap-2">
                  <button
                    class="rounded-md px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                    @click="startMove(item)"
                  >
                    Move
                  </button>
                  <button
                    class="rounded-md px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                    @click="deleteItem(item)"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div
      v-if="moveForm.item"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      @click.self="moveForm.item = null"
    >
      <form
        class="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
        @submit.prevent="moveItem"
      >
        <h2 class="text-lg font-semibold text-slate-900">
          Move “{{ moveForm.item.name }}”
        </h2>
        <label
          class="mt-4 block text-sm font-medium text-slate-700"
          for="move-target"
          >Destination</label
        >
        <select
          id="move-target"
          v-model="moveForm.targetId"
          class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">All policies (root)</option>
          <option
            v-for="folder in moveTargets"
            :key="folder.id"
            :value="String(folder.id)"
          >
            {{ "— ".repeat(folder.depth) }}{{ folder.name }}
          </option>
        </select>
        <p v-if="moveForm.error" class="mt-2 text-sm text-red-600">
          {{ moveForm.error }}
        </p>
        <div class="mt-6 flex justify-end gap-3">
          <button
            type="button"
            class="rounded-lg px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            @click="moveForm.item = null"
          >
            Cancel
          </button>
          <button
            class="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            :disabled="moveForm.saving"
          >
            {{ moveForm.saving ? "Moving…" : "Move" }}
          </button>
        </div>
      </form>
    </div>
  </main>
</template>
